"use client";

import qs from "query-string";
import { useState } from "react";
import OTPLogin from "../otp-login";
import InfoHeader from "./info-header";
import Login from "./login";
import Register from "./register";

type FormSteps = "login" | "register" | "sent";
const LoginForm = ({
  isApp = false,
  redirectUrl,
}: {
  isApp?: boolean;
  redirectUrl?: string;
}) => {
  const [error, setError] = useState<null | { message: string }>(null);
  const [step, setStep] = useState<FormSteps>("login");
  const [email, setEmail] = useState<string | null>(null);

  const resetForm = () => {
    setStep("login");
    setError(null);
    setEmail(null);
  };

  const nextStep = (step: FormSteps) => {
    setError(null);
    setStep(step);
  };

  return (
    <>
      {/* A live region that only mounts once step becomes "sent" adds its
          content in the same DOM mutation as the region itself — most
          screen readers need the region already present to announce a
          later change, so this one stays mounted from the first render. */}
      <div aria-live="polite" className="sr-only">
        {step === "sent" &&
          "Vi har skickat ett e-postmeddelande med en inloggningskod till din e-postadress."}
      </div>

      {step !== "sent" && (
        <div className="space-y-6">
          {step === "login" && (
            <Login
              redirectUrl={redirectUrl}
              isApp={isApp}
              onSuccess={(email, createdUser) => {
                nextStep(createdUser ? "register" : "sent");
                setEmail(email);
              }}
              onError={(message) => setError({ message })}
            />
          )}
          {step === "register" && email && (
            <>
              <Register
                isApp={isApp}
                onSuccess={(auth) => {
                  if (!auth.access_token) {
                    nextStep("sent");
                    return;
                  }

                  if (isApp) {
                    window.location.href = `/login-success?${qs.stringify({
                      accessToken: auth.access_token,
                      refreshToken: auth.refresh_token,
                      redirectUrl,
                    })}`;
                  } else if (redirectUrl) {
                    window.location.href = decodeURI(redirectUrl);
                  } else {
                    window.location.href = "/konto/mitt-efn";
                  }
                }}
                onError={(message) => setError({ message })}
                email={email}
              />
            </>
          )}
          {error?.message && (
            <div
              role="alert"
              className="text-red-700 dark:text-static-white border-2 border-red-500 p-3"
            >
              <p>{error?.message}</p>
            </div>
          )}
        </div>
      )}

      {/* Not a live region: the sr-only region above announces this step,
          and a live region here would also read out every later text change
          inside it (the code field's error, the submit button's loader),
          duplicating what the form's own role="alert" already announces. */}
      {step == "sent" && (
        <div className="space-y-8">
          {!error && <InfoHeader header="Kolla din e-post" description="" />}
          {error && (
            <p className="text-red-500 dark:text-static-white mt-5">
              {error.message}
            </p>
          )}
          <p className="">
            Vi har skickat ett e-postmeddelande till dig från EFN. Ange koden
            som du hittar i meddelandet i fältet nedanför eller klicka på länken
            i meddelandet för att fortsätta.
          </p>

          <OTPLogin
            email={email || undefined}
            redirectUrl={redirectUrl}
            isApp={isApp}
          />

          <p>
            {" "}
            Om du inte hittar mejlet, kontrollera gärna skräppostmappen eller{" "}
            <a
              href="#"
              className="underline text-comp-800 dark:text-static-white"
              onClick={resetForm}
            >
              skicka ett nytt mail.
            </a>
          </p>
        </div>
      )}
    </>
  );
};

export default LoginForm;
